import { BaseExtension } from "@/wc/text-editor/lib/core/base-extension";

import {
    Plugin,
    PluginKey,
    Command,
    TextSelection,
    Transaction,
} from "prosemirror-state";
import { keydownHandler } from "prosemirror-keymap";

import { InputMenuItemState, NodeInsertMenu } from "./component/insert-menu";
import {
    MENU_PLUGIN_KEY,
    MenuItem,
    recursiveMenuItemToState,
} from "@/wc/text-editor/lib/core/menu";
import {
    closePopup,
    getPopupContents,
    getPopupSpec,
    addPopup,
    closeAllPopups,
} from "@/wc/text-editor/lib/core/popups";

export interface MatchInput {
    start: number;
    end: number;
}

interface InsertMenuPluginState {
    isDisabled: boolean;
}

const POPUP_KEY = "insertMenu";

const createMenuKeybindings = (popup: {
    key: string;
}): Record<string, Command> => {
    return {
        up: (state) => {
            const menu = getPopupContents<NodeInsertMenu>(state, popup.key);
            if (menu) {
                menu.up();
                return true;
            }
            return false;
        },
        down: (state) => {
            const menu = getPopupContents<NodeInsertMenu>(state, popup.key);
            if (menu) {
                menu.down();
                return true;
            }
            return false;
        },
        openSubmenu: (state) => {
            const menu = getPopupContents<NodeInsertMenu>(state, popup.key);
            if (menu) {
                menu.openSubmenu();
                return true;
            }
            return false;
        },
        closeSubmenu: (state) => {
            const menu = getPopupContents<NodeInsertMenu>(state, popup.key);
            if (menu) {
                menu.closeSubmenu();
                return true;
            }
            return false;
        },
        select: (state) => {
            const menu = getPopupContents<NodeInsertMenu>(state, popup.key);
            if (menu) {
                menu.runSelection();
                return true;
            }
            return false;
        },
        close: (state, dispatch) => {
            const menu = getPopupContents<NodeInsertMenu>(state, popup.key);
            if (menu) {
                if (menu.closeSubmenu()) {
                    return true;
                }
                if (dispatch) {
                    dispatch(closePopup(state.tr, popup.key));
                }
                return true;
            }
            return false;
        },
    };
};

const slashInsertPlugin = (INSERT_PLUGIN: PluginKey) => {
    const MenuKeybindings = createMenuKeybindings({ key: POPUP_KEY });
    return new Plugin<InsertMenuPluginState>({
        key: INSERT_PLUGIN,
        state: {
            init: () => ({
                isDisabled: false,
            }),
            apply: (_, pluginState) => pluginState,
        },
        appendTransaction(transactions, _oldState, newState) {
            if (INSERT_PLUGIN.getState(newState)?.isDisabled) {
                return closePopup(newState.tr, POPUP_KEY);
            }
            // Get the node at the cursor.
            const { $cursor } = newState.selection as TextSelection;
            if (!$cursor) {
                return;
            }

            for (const transaction of transactions) {
                if (!transaction.docChanged) {
                    continue;
                }
                const match = $cursor.nodeBefore?.textContent.match(/\/$/);
                if (!match) {
                    if (getPopupSpec(newState, POPUP_KEY)) {
                        return closePopup(newState.tr, POPUP_KEY);
                    }
                    continue;
                }
                const popupOpen = Boolean(getPopupSpec(newState, POPUP_KEY));
                const input = {
                    match,
                    start: $cursor.pos - match[0].length,
                    end: $cursor.pos,
                };

                if (!popupOpen) {
                    let transaction = closeAllPopups(newState.tr, newState);
                    const menuState = MENU_PLUGIN_KEY.getState(newState);
                    if (!menuState?.items) return;
                    return addPopup(transaction, POPUP_KEY, {
                        create: (view) => {
                            const newRun = (run: MenuItem["run"]) => {
                                return (event: Event, input_: MatchInput) => {
                                    return run?.(
                                        view.state,
                                        (tr_: Transaction) => {
                                            tr_ = closePopup(tr_, POPUP_KEY);
                                            const length =
                                                input_.end - input_.start;
                                            const mapped_start =
                                                tr_.mapping.map(input_.start);
                                            tr_ = tr_.deleteRange(
                                                mapped_start,
                                                mapped_start + length
                                            );
                                            view.dispatch(tr_);
                                        },
                                        view,
                                        event
                                    );
                                };
                            };

                            return new NodeInsertMenu(
                                menuState.items
                                    .map((item) => {
                                        if (!item.showInInlineMenu) {
                                            return;
                                        }
                                        return recursiveMenuItemToState(
                                            item,
                                            newState,
                                            view,
                                            newRun
                                        ) as InputMenuItemState;
                                    })
                                    .filter((i) => !!i),
                                input,
                                view
                            );
                        },
                        startPos: input.start,
                        closeOnMouseLeave: false,
                        onlyInEditMode: true,
                    });
                } else {
                    // Set correct anchor for the menu.
                    const popupSpec = getPopupSpec(newState, POPUP_KEY);
                    if (!popupSpec) return;
                    popupSpec.startPos = input.start;
                    popupSpec.endPos = input.end;

                    const menu = getPopupContents<NodeInsertMenu>(
                        newState,
                        POPUP_KEY
                    );
                    if (menu) {
                        // Filter the popup menu.
                        menu.input = input;
                        return newState.tr;
                    }
                }
            }
        },
        view() {
            return {
                update(view) {
                    const pluginState = INSERT_PLUGIN.getState(view.state);
                    pluginState.isDisabled = !view.editable;
                },
            };
        },

        props: {
            handleKeyDown: keydownHandler({
                ArrowUp: MenuKeybindings.up,
                ArrowDown: MenuKeybindings.down,
                ArrowRight: MenuKeybindings.openSubmenu,
                ArrowLeft: MenuKeybindings.closeSubmenu,
                Enter: MenuKeybindings.select,
                Tab: MenuKeybindings.select,
                Escape: MenuKeybindings.close,
            }),
            handleClick: (view) => {
                view.dispatch(closePopup(view.state.tr, POPUP_KEY));
            },
        },
    });
};

const nodeInsertPlugins = () => {
    const INSERT_PLUGIN = new PluginKey("INSERT_PLUGIN");
    return [slashInsertPlugin(INSERT_PLUGIN)];
};

export { nodeInsertPlugins };

export class InlineInsertMenu extends BaseExtension {
    plugins = nodeInsertPlugins;
}
